import type { MessageKey } from '@autho/i18n';
import {
  AlarmClock,
  Bot,
  Brain,
  Building2,
  CalendarDays,
  CircleHelp,
  Cog,
  FileText,
  Files,
  Handshake,
  Landmark,
  Mail,
  MessageSquare,
  Plug,
  ScrollText,
  Settings,
  ShieldCheck,
  Target,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import type { ProfileKind } from '../lib/profile.ts';
import { belongsTo } from '../lib/profile.ts';

export interface MenuLink {
  to: string;
  label: MessageKey;
  icon: typeof Building2;
}
export interface MenuGroup {
  label: MessageKey;
  items: MenuLink[];
}
export type MenuEntry = MenuLink | MenuGroup;
export const isGroup = (entry: MenuEntry): entry is MenuGroup => 'items' in entry;

/**
 * Every screen in the menu, in the order D73 sets: Needs you, then Mind, Beliefs, Sources, Access, Engine and
 * Settings (Personal build step 1). *Needs you* is the landing page of a person's own profile and stands above Mind,
 * not inside it (D74). **Sources** is every connection the profile holds, one row each, with what it feeds and a
 * pause: it left Mind, where it was *Connected accounts*, because a source is not a domain of what Autho knows but
 * where that knowledge comes from (F374). **Chat** stands after Mind (the Chat release, slice 6). **Work is absent because it does not exist yet** — an entry for a screen that is not
 * built teaches a person that the product is broken rather than unfinished. Which profile shows which entry is not
 * written here but read from `lib/profile.ts`, the table the route guard and the command palette read too (R4).
 */
const NAV: MenuEntry[] = [
  // First in a person's own profile: it is the question a person opens the app with (Batch E.2, D74).
  { to: '/attention', label: 'nav.attention', icon: AlarmClock },
  {
    label: 'nav.mind',
    items: [
      { to: '/structure', label: 'nav.structure', icon: Building2 },
      // Mind's domains, in the order D51 sets: customers and suppliers first, from the accounting data
      // already synced. Bank and treasury, then correspondence, arrive with their sources.
      { to: '/counterparties', label: 'nav.counterparties', icon: Handshake },
      // Understanding, in the order the Personal profile names it: who I am, the people, what I own and owe
      // (Personal step 3, by hand).
      { to: '/profile', label: 'nav.profile', icon: UserRound },
      // Me's Relationships domain, counted from those accounts' headers (Batch C.3).
      { to: '/relationships', label: 'nav.relationships', icon: Users },
      { to: '/finances', label: 'nav.finances', icon: Wallet },
      // Goals close Understanding, as the Personal profile orders it (Personal step 4).
      { to: '/goals', label: 'nav.goals', icon: Target },
      // The Record, after the people it is about (Personal step 2): what the person's accounts hold, newest first.
      { to: '/conversations', label: 'nav.conversations', icon: Mail },
      { to: '/calendar', label: 'nav.calendar', icon: CalendarDays },
      { to: '/files', label: 'nav.files', icon: Files },
      { to: '/bank', label: 'nav.bank', icon: Landmark },
      { to: '/documents', label: 'nav.documents', icon: FileText },
      { to: '/clarify', label: 'nav.clarify', icon: CircleHelp },
    ],
  },
  // Chat (Chat release slice 6): after Mind and before Beliefs, as the layout sets it; in both profiles, each with its
  // own threads and its own Autho Chat.
  { to: '/chat', label: 'nav.chat', icon: MessageSquare },
  { to: '/beliefs', label: 'nav.beliefs', icon: Brain },
  // Kept at its address, so every link and bookmark to *Connected accounts* still opens it.
  { to: '/accounts', label: 'nav.sources', icon: Plug },
  {
    label: 'nav.access',
    items: [
      { to: '/audit', label: 'nav.audit', icon: ScrollText },
      { to: '/configure/agents', label: 'nav.agents', icon: Bot },
      // What agents ask to do, and the switch that stops them (F369): an Org's, where the rule lives.
      { to: '/approvals', label: 'nav.approvals', icon: ShieldCheck },
    ],
  },
  { to: '/configure', label: 'nav.engine', icon: Cog },
  { to: '/settings', label: 'nav.settings', icon: Settings },
];

/** What else decides the menu besides the profile's kind: Chat, open only to the people the service names (F441). */
export interface MenuFor {
  chat: boolean;
}

const shown = (kind: ProfileKind, to: string, open: MenuFor) =>
  belongsTo(kind, to) && (to !== '/chat' || open.chat);

/** One profile's menu: each entry whose screen that kind of profile has, and no group left with nothing in it. */
export function menuFor(kind: ProfileKind, open: MenuFor = { chat: true }): MenuEntry[] {
  return NAV.flatMap((entry): MenuEntry[] => {
    if (!isGroup(entry)) return shown(kind, entry.to, open) ? [entry] : [];
    const items = entry.items.filter((item) => shown(kind, item.to, open));
    return items.length > 0 ? [{ ...entry, items }] : [];
  });
}

/** The same screens, in the same order, flat: what the command palette offers. */
export function screensFor(kind: ProfileKind, open: MenuFor = { chat: true }): MenuLink[] {
  return menuFor(kind, open).flatMap((entry) => (isGroup(entry) ? entry.items : [entry]));
}
